const { normalizePath } = require("@qlik/utils/normalizePathOrUrl");

/**
 *  A config object that maps langcode to language info for all enabled languages on the site, e.g.
 *
 *   {
 *     en: { langName: "English", enLangName: "English", langCode: "en", pathPrefix: "", locale: "en-US" },
 *     fr: { langName: "Français", enLangName: "French", langCode: "fr", pathPrefix: "/fr", locale: "fr-FR" },
 *     ja: { langName: "日本語", enLangName: "Japanese", langCode: "ja", pathPrefix: "/jp", locale: "ja-JP" },
 *   }
 *
 */
const i18nConfig = require("@qlik/qlik/src/config/i18n");

/**
 * Return an object with some helper info for handling i18n. Generally this function should
 * be avoided and the I18nContext should be used instead (getI18nHelper is called in the page
 * template with the appropriate path and contentful locale, and passed to the context).
 *
 * @param contentfulLocale
 *   "Locale" coming from contentful, not necessarily ISO 3166 compliant.
 */
const createI18nHelper = (contentfulLocale = "en") => {
    // Some base i18n helper functionality.
    const i18nHelper = {
        languages: Object.keys(i18nConfig),
        map: i18nConfig,
    };

    // Chainable lang info getter, e.g. i18n.get("langName").from("pathPrefix", "/jp") would return "日本語"
    i18nHelper.get = (getProperty) => ({
        from: (fromProperty, fromValue) =>
            Object.values(i18nHelper.map).find((el) => el[fromProperty] === fromValue)?.[getProperty],
    });

    // Will return a localized date string, according to langName (ie: "Français")
    i18nHelper.getLocalisedDate = (dateStrStart, dateStrEnd = null, langName) => {
        if (!dateStrStart) return null;

        const dateOpts = { month: "long", day: "numeric", year: "numeric", timeZone: "UTC" };
        const startDate = new Date(dateStrStart).toLocaleDateString(
            i18nHelper.get("locale").from("langName", langName),
            dateOpts
        );
        let endDate = "";
        if (dateStrEnd) {
            endDate = ` - ${new Date(dateStrEnd).toLocaleDateString(
                i18nHelper.get("locale").from("langName", langName),
                dateOpts
            )}`;
        }

        return startDate + endDate;
    };

    // Grab the value from the map above that corresponds to the *current* locale.
    i18nHelper.curr = i18nHelper.map[contentfulLocale];

    // Method for determining whether a path already has a language prefix.
    i18nHelper.pathHasLangPrefix = (path) => {
        const langPrefixes = {
            ...Object.values(i18nHelper.map)
                .map((el) => el.pathPrefix)
                .filter((el) => el),
        };

        const result = Object.values(langPrefixes).filter((prefix) => {
            return prefix === path || path?.startsWith(`${prefix}/`);
        });

        return Boolean(result.length);
    };

    // Return the lang prefix for the given path if it exists, otherwise undefined.
    i18nHelper.getPathLangPrefix = (path) => {
        const langPrefixes = {
            ...Object.values(i18nHelper.map)
                .map((el) => el.pathPrefix)
                .filter((el) => el),
        };

        const result = Object.values(langPrefixes).filter((prefix) => {
            return prefix === path || path?.startsWith(`${prefix}/`);
        })?.[0];

        return result;
    };

    // Method for determining whether a path already has a language prefix.
    i18nHelper.getUnPrefixedPath = (path) => {
        const langPrefixes = {
            ...Object.values(i18nHelper.map)
                .map((el) => el.pathPrefix)
                .filter((el) => el),
        };

        const prefix = Object.values(langPrefixes).filter((prefix) => {
            return prefix === path || path?.startsWith(`${prefix}/`);
        })?.[0];

        return prefix ? normalizePath(path.split(prefix)[1]) : path;
    };

    /**
     * Returns a path in combination with the passed locale, takes ja/jp into account.
     *
     * @param path
     *   Full path to the entry, including any prefixes (ie: "/blog/").
     * @param locale
     *   ISO 639 language code (called locale by contentful). This should be the locale code as specified in contentful.
     */
    i18nHelper.getLangPrefixedPath = (path, locale = contentfulLocale) => {
        // Blog paths shouldn't be prefixed.
        const normalizedPath = normalizePath(path);
        if (normalizedPath?.startsWith("/blog/")) return normalizedPath;

        // Get the appropriate prefix.
        const prefix = i18nHelper.get("pathPrefix").from("contentfulCode", locale);

        // Remove repeating slashes.
        return normalizePath(`/${prefix}/${path}/`);
    };

    return i18nHelper;
};

module.exports.createI18nHelper = createI18nHelper;
module.exports.i18nConfig = i18nConfig;
